import React, { useState, useEffect, useMemo, useRef } from "react"
import { useLocation, useNavigate, Link } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  Grid,
  TextField,
  Typography,
  Button,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  InputAdornment,
  Chip,
  Avatar,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import SearchIcon from "@mui/icons-material/Search"
import CloseIcon from "@mui/icons-material/Close"
import SaveIcon from "@mui/icons-material/Save"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import InventoryIcon from "@mui/icons-material/Inventory"
import CategoryIcon from "@mui/icons-material/Category"
import SyncAltIcon from "@mui/icons-material/SyncAlt"
import ImageIcon from "@mui/icons-material/Image"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import BuildIcon from "@mui/icons-material/Build"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing"
import SettingsIcon from "@mui/icons-material/Settings"
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices"
import StraightenIcon from "@mui/icons-material/Straighten"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera"
import Inventory2Icon from "@mui/icons-material/Inventory2"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import EditIcon from "@mui/icons-material/Edit"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import StorefrontIcon from "@mui/icons-material/Storefront"
import ViewModuleIcon from "@mui/icons-material/ViewModule"
import LayersIcon from "@mui/icons-material/Layers"
import ViewListIcon from "@mui/icons-material/ViewList"
import Collapse from "@mui/material/Collapse"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableRow from "@mui/material/TableRow"

import {
  patchProduct,
  getTypes,
  getMaterials,
  getProducts,
  getProviders,
  getVatCategories,
} from "../../helpers/api_helper"
import { getCustomers as onGetCustomers } from "store/actions"
import CustomerSelect from "pages/Orders/customersmodal"
import ViewInArIcon from "@mui/icons-material/ViewInAr"
import StepFileUpload from "./StepFileUpload"
import ProductImageManager from "./ProductImageManager"
import Pytha3DFileUpload from "./Pytha3DFileUpload"
import { uploadFile, getEntityFiles } from "../../helpers/fileStorage"
import ProductCategoryAssigner from "components/ProductCategoryAssigner/ProductCategoryAssigner"

// Styled Components
const StyledStepCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: "#cbd5e1",
  },
}))

const MaterialChip = styled(Chip)(({ materialType }) => {
  const colors = {
    MATERIAL: { bg: "#dbeafe", border: "#93c5fd", text: "#1d4ed8" },
    "COMPLETE-PRODUCT": { bg: "#dcfce7", border: "#86efac", text: "#16a34a" },
    "PURCHASED-MATERIAL": { bg: "#fef3c7", border: "#fcd34d", text: "#d97706" },
  }
  const colorSet = colors[materialType] || colors.MATERIAL

  return {
    backgroundColor: colorSet.bg,
    border: `1px solid ${colorSet.border}`,
    color: colorSet.text,
    fontWeight: 500,
    fontSize: "0.75rem",
    "& .MuiChip-icon": { color: colorSet.text },
  }
})


const MuiProductUpdate = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  document.title = t("Update Product") + " | FlexFlow ERP"

  // Form State
  const [name, setName] = useState("")
  const [prdId, setPrdId] = useState()
  const [sku, setSku] = useState("")
  const [provsku, setProvSku] = useState("")
  const [vat, setVat] = useState("")
  const [description, setDescription] = useState("")
  const [b2bSalesPrice, setB2bSalesPrice] = useState("")
  const [salesPrice, setSalesPrice] = useState("")
  const [vatClass, setVatClass] = useState("")
  const [vatCategoryId, setVatCategoryId] = useState("")
  const [vatCategories, setVatCategories] = useState([])
  const [stock, setStock] = useState(0)
  const [unit, setUnit] = useState("Item")
  const [categories, setCategories] = useState([])
  const [usageType, setUsageType] = useState("MATERIAL")
  const [costPrice, setCostPrice] = useState("")
  const [costMargin, setCostMargin] = useState("")
  const [overrideable, setOverrideable] = useState(false)
  const [canLended, setCanLended] = useState(false)
  const [customerId, setCustomerId] = useState("")
  const [customerName, setCustomerName] = useState("")
  const [productPhotos, setProductPhotos] = useState([])

  // Product Dimensions
  const [productDepth, setProductLength] = useState("")
  const [productWidth, setProductWidth] = useState("")
  const [productHeight, setProductHeight] = useState("")
  const [productWeight, setProductWeight] = useState("")

  // Packaging Dimensions
  const [packagingLength, setPackagingLength] = useState("")
  const [packagingWidth, setPackagingWidth] = useState("")
  const [packagingHeight, setPackagingHeight] = useState("")
  const [packagingWeight, setPackagingWeight] = useState("")

  // Steps/Materials State
  const [steps, setSteps] = useState([])
  const [types, setTypes] = useState([])
  const [materialsList, setMaterialsList] = useState([])
  const [selectedStep, setSelectedStep] = useState(null)
  const [newStepType, setNewStepType] = useState("")

  // Service Tasks State
  const [serviceTasks, setServiceTasks] = useState([])
  const [editingTaskIndex, setEditingTaskIndex] = useState(null)
  const [taskDialogOpen, setTaskDialogOpen] = useState(false)
  const [currentTask, setCurrentTask] = useState({
    name: "",
    description: "",
    duration: "",
    price: "",
    file: null,
    photo: null,
  })

  // Providers State (for PURCHASED-MATERIAL)
  const [providersList, setProvidersList] = useState([])
  const [selectedProviders, setSelectedProviders] = useState([])
  const [providerDrawerOpen, setProviderDrawerOpen] = useState(false)
  const [providerSearch, setProviderSearch] = useState("")

  // Compiled Items State (for COMPILED-PRODUCT)
  const [compiledItems, setCompiledItems] = useState([])
  const [compilerDrawerOpen, setCompilerDrawerOpen] = useState(false)
  const [compilerSearch, setCompilerSearch] = useState("")
  const [availableProducts, setAvailableProducts] = useState([])

  // Compilable With State (for COMPLETE-PRODUCT - references to what this product can be compiled with)
  const [canBeCompiled, setCanBeCompiled] = useState(false)
  const [compilableWith, setCompilableWith] = useState([])
  const [compilableDrawerOpen, setCompilableDrawerOpen] = useState(false)
  const [compilableSearch, setCompilableSearch] = useState("")
  const [availableCompleteProducts, setAvailableCompleteProducts] = useState([])

  // Variants State
  const [variants, setVariants] = useState([])
  const [showVariants, setShowVariants] = useState(false)

  // Pytha 3D State
  const [showPytha3D, setShowPytha3D] = useState(false)
  const [pytha3DFile, setPytha3DFile] = useState(null)

  // UI State
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })
  const [updateAllLoader, setUpdateAllLoader] = useState("")
  const [showDimensions, setShowDimensions] = useState(false)
  const [showOptions, setShowOptions] = useState(false)

  const initialLoadComplete = useRef(false)

  // Redux
  const customers = useSelector((state) => state.customers.customers)

  // Options
  const unitOptions = [
    { value: "KG", label: t("Kilograms") },
    { value: "Meter", label: t("Meters") },
    { value: "CM", label: t("Centimeters") },
    { value: "MM", label: t("Millimeters") },
    { value: "Litter", label: t("Liters") },
    { value: "CubicM", label: t("Cubic Meters") },
    { value: "Item", label: t("Items") },
    { value: "Gram", label: t("Grams") },
  ]

  const usageTypeOptions = [
    { value: "MATERIAL", label: t("Material"), icon: <BuildIcon fontSize="small" /> },
    { value: "PURCHASED-MATERIAL", label: t("Purchased Material"), icon: <PrecisionManufacturingIcon fontSize="small" /> },
    { value: "COMPLETE-PRODUCT", label: t("Complete Product"), icon: <ShoppingCartIcon fontSize="small" /> },
    { value: "SERVICE", label: t("Service"), icon: <MiscellaneousServicesIcon fontSize="small" /> },
    { value: "COMPILED-PRODUCT", label: t("Compiled Product"), icon: <LayersIcon fontSize="small" /> },
  ]

  // Fetch initial data
  useEffect(() => {
    dispatch(onGetCustomers())
    getTypes().then((res) => {
      const typesData = res || []
      setTypes(typesData)
      if (typesData.length > 0 && !newStepType) {
        setNewStepType(typesData[0].name)
      }
    })
    getMaterials().then((res) => setMaterialsList(res || []))
    getProviders().then((res) => setProvidersList(res || []))
    // Fetch products for compiler (Complete Products and Services) and compilable with (Complete Products only)
    getProducts().then((res) => {
      const products = res || []
      const filteredProducts = products.filter(
        (p) => p.usageType === "COMPLETE-PRODUCT" || p.usageType === "SERVICE"
      )
      setAvailableProducts(filteredProducts)
      // For "compilable with" - only Complete Products
      const completeProducts = products.filter((p) => p.usageType === "COMPLETE-PRODUCT")
      setAvailableCompleteProducts(completeProducts)
    })
    // Fetch VAT categories
    getVatCategories().then((res) => {
      setVatCategories((res || []).filter(cat => cat.is_active !== false))
    }).catch((err) => {
      console.error("Failed to fetch VAT categories:", err)
    })
  }, [dispatch])

  // Load product data from location state
  useEffect(() => {
    const prd = location.state?.product
    const fromVisualEditor = location.state?.fromVisualEditor

    // If coming back from Visual Editor, only update steps
    if (fromVisualEditor && prd?.requirements) {
      try {
        setSteps(JSON.parse(prd.requirements))
      } catch {
        // Keep existing steps if parse fails
      }
      return
    }

    if (prd) {
      setName(prd.name || "")
      setSku(prd.sku || "")
      setProvSku(prd.provsku || "")
      setVat(prd.vat || "")
      setDescription(prd.description || "")
      setB2bSalesPrice(prd.b2bsalesprice || "")
      setSalesPrice(prd.salesprice || "")
      setVatClass(prd.vatClass || "")
      setVatCategoryId(prd.vat_category_id || "")
      setStock(prd.stock || 0)
      setUnit(prd.unit || "Item")
      // Parse categories - handle both JSON array and legacy string formats
      if (prd.category) {
        try {
          const parsed = JSON.parse(prd.category)
          setCategories(Array.isArray(parsed) ? parsed : [prd.category])
        } catch {
          // Not JSON, treat as single category string
          setCategories([prd.category])
        }
      } else {
        setCategories([])
      }
      setUsageType(prd.usageType || "MATERIAL")
      setCostMargin(prd.costmargin || "")
      setCostPrice(prd.costprice || "")
      setOverrideable(prd.overidable || false)
      setCanLended(prd.canlended || false)
      setPrdId(prd.id)
      setCustomerId(prd.customer_id || "")

      // Product Dimensions
      setProductLength(prd.productDepth || "")
      setProductWidth(prd.productWidth || "")
      setProductHeight(prd.productHeight || "")
      setProductWeight(prd.productWeight || "")

      // Packaging Dimensions
      setPackagingLength(prd.packagingLength || "")
      setPackagingWidth(prd.packagingWidth || "")
      setPackagingHeight(prd.packagingHeight || "")
      setPackagingWeight(prd.packagingWeight || "")

      if (prd.productPhotos) {
        try {
          setProductPhotos(JSON.parse(prd.productPhotos))
        } catch {
          setProductPhotos([])
        }
      }
      if (prd.requirements) {
        try {
          setSteps(JSON.parse(prd.requirements))
        } catch {
          setSteps([])
        }
      }

      if (prd.serviceTasks) {
        try {
          setServiceTasks(JSON.parse(prd.serviceTasks))
        } catch {
          setServiceTasks([])
        }
      }

      if (prd.providerIds) {
        try {
          setSelectedProviders(JSON.parse(prd.providerIds))
        } catch {
          setSelectedProviders([])
        }
      }

      if (prd.compiledItems) {
        try {
          setCompiledItems(JSON.parse(prd.compiledItems))
        } catch {
          setCompiledItems([])
        }
      }

      // Load "compilable with" data for Complete Products
      setCanBeCompiled(prd.canBeCompiled || false)
      if (prd.compilableWith) {
        try {
          setCompilableWith(JSON.parse(prd.compilableWith))
        } catch {
          setCompilableWith([])
        }
      }

      // Load variants
      if (prd.variants) {
        try {
          const parsed = JSON.parse(prd.variants)
          if (Array.isArray(parsed)) {
            setVariants(parsed.filter(v => v && typeof v === "object").map(v => ({
              sku: v.sku ?? "",
              depth: v.depth ?? "",
              width: v.width ?? "",
              height: v.height ?? "",
              salesprice: v.salesprice ?? "",
              b2bsalesprice: v.b2bsalesprice ?? "",
              stock: v.stock ?? 0,
            })))
          } else {
            setVariants([])
          }
        } catch {
          setVariants([])
        }
      }

      setTimeout(() => {
        initialLoadComplete.current = true
      }, 100)
    } else {
      initialLoadComplete.current = true
    }
  }, [location.state])

  // Update customer name when customerId changes
  useEffect(() => {
    if (customerId && customers) {
      const customer = customers.find((c) => c.id === customerId)
      if (customer) {
        setCustomerName(customer.name)
      }
    }
  }, [customerId, customers])

  // Load existing Pytha 3D file
  useEffect(() => {
    if (!prdId) return
    getEntityFiles("product-pytha3d", prdId).then((files) => {
      if (files?.length > 0) {
        setPytha3DFile(files[0])
        setShowPytha3D(true)
      }
    })
  }, [prdId])

  // Recalculate cost when steps or margin changes
  useEffect(() => {
    if (usageType !== "PURCHASED-MATERIAL" && usageType !== "COMPILED-PRODUCT" && steps.length > 0) {
      calculateTotalCost()
    }
  }, [steps, costMargin])

  // Recalculate cost for COMPILED-PRODUCT when items or margin changes
  useEffect(() => {
    if (!initialLoadComplete.current) return
    if (usageType === "COMPILED-PRODUCT") {
      const total = compiledItems.reduce(
        (sum, item) => sum + (parseFloat(item.costprice) || 0) * (item.quantity || 1),
        0
      )
      const margin = parseFloat(costMargin) || 0
      const finalCost = total + (total * margin) / 100
      setCostPrice(finalCost.toFixed(2))
    }
  }, [compiledItems, costMargin, usageType])

  // Handle usage type change
  useEffect(() => {
    if (!initialLoadComplete.current) return
    if (usageType === "COMPILED-PRODUCT") {
      // For compiled products, cost is calculated from compiled items
      const total = compiledItems.reduce(
        (sum, item) => sum + (parseFloat(item.costprice) || 0) * (item.quantity || 1),
        0
      )
      const margin = parseFloat(costMargin) || 0
      const finalCost = total + (total * margin) / 100
      setCostPrice(finalCost.toFixed(2))
    } else if (usageType !== "PURCHASED-MATERIAL") {
      if (steps.length > 0) {
        calculateTotalCost()
      } else {
        setCostPrice(0)
      }
    }
  }, [usageType])

  const calculateTotalCost = async () => {
    const matSum = []
    for (const step of steps) {
      for (const mat of step.materials || []) {
        try {
          const res = await getMaterials(mat.id)
          if (res && res[0]) {
            const matCost = mat.quantity * res[0].costprice
            matSum.push(matCost)
          }
        } catch (err) {
          console.error("Error fetching material:", err)
        }
      }
    }
    const total = matSum.reduce((a, b) => a + b, 0)
    const margin = parseFloat(costMargin) || 0
    setCostPrice(total + (total / 100) * margin)
  }

  // Filtered materials for drawer
  const filteredMaterials = useMemo(() => {
    const base = materialsList.filter(
      (mat) => mat.usageType === "MATERIAL" || mat.usageType === "PURCHASED-MATERIAL"
    )
    if (!search) return base
    const searchRegex = new RegExp(search, "i")
    return base.filter(
      (mat) => searchRegex.test(mat.name) || searchRegex.test(mat.description || "")
    )
  }, [search, materialsList])

  // Handlers
  const handleAddStep = () => {
    setSteps((prev) => [
      ...prev,
      { seq: prev.length + 1, typid: newStepType, materials: [], files: [] },
    ])
  }

  const handleStepFilesChange = (stepIndex, newFiles) => {
    const newSteps = [...steps]
    newSteps[stepIndex].files = newFiles
    setSteps(newSteps)
  }

  const handleStepInstructionsChange = (stepIndex, instructions) => {
    const newSteps = [...steps]
    newSteps[stepIndex].instructions = instructions
    setSteps(newSteps)
  }

  const handleDeleteStep = (index) => {
    setSteps((prev) => prev.filter((_, i) => i !== index))
    setCostPrice(0)
    calculateTotalCost()
  }

  const handleDeleteMaterial = (stepIndex, materialIndex) => {
    const newSteps = [...steps]
    newSteps[stepIndex].materials.splice(materialIndex, 1)
    setSteps(newSteps)
    calculateTotalCost()
  }

  const handleMaterialQuantityChange = (stepIndex, materialIndex, quantity) => {
    const newSteps = [...steps]
    newSteps[stepIndex].materials[materialIndex].quantity = quantity.replace(",", ".")
    setSteps(newSteps)
  }

  const handleSelectMaterial = (material) => {
    if (selectedStep !== null) {
      const newSteps = [...steps]
      newSteps[selectedStep].materials.push({ ...material, quantity: 0 })
      setSteps(newSteps)
      setDrawerOpen(false)
      setSearch("")
    }
  }

  const handleSave = async () => {
    if (!prdId) {
      setSnackbar({ open: true, message: t("No product ID found"), severity: "error" })
      return
    }

    setIsSaving(true)
    try {
      const payload = {
        resource: [
          {
            id: prdId,
            name: name || "",
            description: description || "",
            b2bsalesprice: b2bSalesPrice || null,
            sku: sku || "",
            vat: vat || "",
            vat_category_id: vatCategoryId || null,
            productPhotos: JSON.stringify(productPhotos || []),
            usageType: usageType || "MATERIAL",
            salesprice: salesPrice || null,
            provsku: provsku || "",
            costprice: costPrice || null,
            vatClass: vatClass || "",
            stock: stock || 0,
            unit: unit || "Item",
            category: categories.length > 0 ? JSON.stringify(categories) : "",
            requirements: JSON.stringify(steps || []),
            costmargin: costMargin || null,
            overidable: overrideable || false,
            canlended: canLended || false,
            customer_id: customerId || null,
            productDepth: productDepth || null,
            productWidth: productWidth || null,
            productHeight: productHeight || null,
            productWeight: productWeight || null,
            packagingLength: packagingLength || null,
            packagingWidth: packagingWidth || null,
            packagingHeight: packagingHeight || null,
            packagingWeight: packagingWeight || null,
            serviceTasks: JSON.stringify(serviceTasks || []),
            providerIds: JSON.stringify(selectedProviders || []),
            compiledItems: JSON.stringify(compiledItems || []),
            canBeCompiled: canBeCompiled || false,
            compilableWith: JSON.stringify(compilableWith || []),
            variants: JSON.stringify(variants || []),
          },
        ],
      }

      const res = await patchProduct(payload)

      if (res && res.error) {
        let errorMessage = res.error.message || t("Unknown error")
        if (res.error.context?.resource?.[0]?.code === "23505") {
          errorMessage = t("A product with this SKU already exists")
        } else if (res.error.context?.resource?.[0]?.message?.includes("duplicate key")) {
          errorMessage = t("A product with this SKU already exists")
        }
        setSnackbar({ open: true, message: errorMessage, severity: "error" })
      } else if (res && res.resource && res.resource[0]) {
        setSnackbar({ open: true, message: t("Product updated successfully"), severity: "success" })
        navigate("/products")
      } else {
        setSnackbar({ open: true, message: t("Unexpected response from server"), severity: "warning" })
      }
    } catch (err) {
      console.error("Save error:", err)
      setSnackbar({ open: true, message: `${t("Error updating product")}: ${err.message}`, severity: "error" })
    } finally {
      setIsSaving(false)
    }
  }

  const handleUpdateAll = async () => {
    const product = location.state?.product
    if (!product) return

    setIsLoading(true)
    try {
      const products = await getProducts()
      let allUpdated = true

      for (const prod of products) {
        if (prod.requirements) {
          const requirements = JSON.parse(prod.requirements)
          let updated = false

          for (const req of requirements) {
            if (req.materials) {
              const matIndex = req.materials.findIndex((m) => m.id === product.id)
              if (matIndex !== -1) {
                req.materials[matIndex] = { ...product, quantity: req.materials[matIndex].quantity }
                updated = true
              }
            }
          }

          if (updated) {
            setUpdateAllLoader(`${t("Updating")}: ${prod.name}`)
            prod.requirements = JSON.stringify(requirements)
            const res = await patchProduct({ resource: [prod] })
            if (res?.error) allUpdated = false
          }
        }
      }

      setSnackbar({
        open: true,
        message: allUpdated ? t("All products updated successfully") : t("Some updates failed"),
        severity: allUpdated ? "success" : "warning",
      })
    } catch (err) {
      setSnackbar({ open: true, message: t("Error updating products"), severity: "error" })
    } finally {
      setIsLoading(false)
      setUpdateAllLoader("")
    }
  }

  // Service Task Handlers
  const handleOpenTaskDialog = (index = null) => {
    if (index !== null) {
      setCurrentTask({ ...serviceTasks[index] })
      setEditingTaskIndex(index)
    } else {
      setCurrentTask({
        name: "",
        description: "",
        duration: "",
        price: "",
        file: null,
        photo: null,
      })
      setEditingTaskIndex(null)
    }
    setTaskDialogOpen(true)
  }

  const handleCloseTaskDialog = () => {
    setTaskDialogOpen(false)
    setEditingTaskIndex(null)
    setCurrentTask({
      name: "",
      description: "",
      duration: "",
      price: "",
      file: null,
      photo: null,
    })
  }

  const handleSaveTask = () => {
    if (!currentTask.name.trim()) {
      setSnackbar({ open: true, message: t("Task name is required"), severity: "error" })
      return
    }

    if (editingTaskIndex !== null) {
      const updatedTasks = [...serviceTasks]
      updatedTasks[editingTaskIndex] = currentTask
      setServiceTasks(updatedTasks)
    } else {
      setServiceTasks([...serviceTasks, currentTask])
    }
    handleCloseTaskDialog()
  }

  const handleDeleteTask = (index) => {
    setServiceTasks(serviceTasks.filter((_, i) => i !== index))
  }

  const handleTaskFileUpload = async (e, type) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const folder = type === "photo" ? "service-task-photos" : "service-task-files"
      const result = await uploadFile(file, folder)
      setCurrentTask({ ...currentTask, [type]: result.url })
    } catch (error) {
      console.error("Upload error:", error)
      setSnackbar({ open: true, message: t("Failed to upload file"), severity: "error" })
    }
  }

  const calculateServiceTotal = () => {
    return serviceTasks.reduce((sum, task) => sum + (parseFloat(task.price) || 0), 0)
  }

  // Provider Handlers
  const handleAddProvider = (provider) => {
    if (!selectedProviders.find((p) => p.id === provider.id)) {
      setSelectedProviders([...selectedProviders, { id: provider.id, name: provider.name }])
    }
    setProviderDrawerOpen(false)
    setProviderSearch("")
  }

  const handleRemoveProvider = (providerId) => {
    setSelectedProviders(selectedProviders.filter((p) => p.id !== providerId))
  }

  const filteredProviders = providersList.filter((provider) =>
    provider.name?.toLowerCase().includes(providerSearch.toLowerCase())
  )

  // Compiled Items Handlers (for COMPILED-PRODUCT)
  const handleAddCompiledItem = (product) => {
    if (!compiledItems.find((item) => item.id === product.id)) {
      const photos = product.productPhotos ? JSON.parse(product.productPhotos) : []
      setCompiledItems([
        ...compiledItems,
        {
          id: product.id,
          name: product.name,
          usageType: product.usageType,
          costprice: product.costprice || 0,
          salesprice: product.salesprice || 0,
          photo: photos[0]?.url || null,
          productDepth: product.productDepth || null,
          productWidth: product.productWidth || null,
          productHeight: product.productHeight || null,
          quantity: 1,
        },
      ])
    }
    setCompilerDrawerOpen(false)
    setCompilerSearch("")
  }

  const handleRemoveCompiledItem = (itemId) => {
    setCompiledItems(compiledItems.filter((item) => item.id !== itemId))
  }

  const handleCompiledItemQuantityChange = (itemId, quantity) => {
    setCompiledItems(
      compiledItems.map((item) =>
        item.id === itemId ? { ...item, quantity: parseFloat(quantity) || 1 } : item
      )
    )
  }

  const filteredAvailableProducts = availableProducts.filter(
    (product) =>
      product.name?.toLowerCase().includes(compilerSearch.toLowerCase()) &&
      product.id !== prdId // Exclude current product
  )

  // Calculate compiled product total cost
  const calculateCompiledTotal = () => {
    const total = compiledItems.reduce(
      (sum, item) => sum + (parseFloat(item.costprice) || 0) * (item.quantity || 1),
      0
    )
    const margin = parseFloat(costMargin) || 0
    return total + (total * margin) / 100
  }

  // Compilable With Handlers (for COMPLETE-PRODUCT)
  const handleAddCompilableProduct = (product) => {
    if (!compilableWith.find((item) => item.id === product.id)) {
      const photos = product.productPhotos ? JSON.parse(product.productPhotos) : []
      setCompilableWith([
        ...compilableWith,
        {
          id: product.id,
          name: product.name,
          sku: product.sku,
          photo: photos[0]?.url || null,
          productDepth: product.productDepth || null,
          productWidth: product.productWidth || null,
          productHeight: product.productHeight || null,
        },
      ])
    }
    setCompilableDrawerOpen(false)
    setCompilableSearch("")
  }

  const handleRemoveCompilableProduct = (productId) => {
    setCompilableWith(compilableWith.filter((item) => item.id !== productId))
  }

  const filteredCompilableProducts = availableCompleteProducts.filter(
    (product) =>
      product.name?.toLowerCase().includes(compilableSearch.toLowerCase()) &&
      product.id !== prdId // Exclude current product
  )

  const getMaterialTypeIcon = (type) => {
    switch (type) {
      case "MATERIAL":
        return <BuildIcon fontSize="small" />
      case "COMPLETE-PRODUCT":
        return <ShoppingCartIcon fontSize="small" />
      case "PURCHASED-MATERIAL":
        return <PrecisionManufacturingIcon fontSize="small" />
      default:
        return <InventoryIcon fontSize="small" />
    }
  }

  return (
    <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Compact Header */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "#64748b" }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" sx={{ color: "#94a3b8" }} />}>
              <Link to="/products" style={{ color: "#64748b", textDecoration: "none", fontSize: "0.875rem" }}>
                {t("Products")}
              </Link>
              <Typography color="#1e293b" fontSize="0.875rem" fontWeight={500}>
                {name || t("Update Product")}
              </Typography>
            </Breadcrumbs>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate("/products")}
              sx={{
                borderColor: "#e2e8f0",
                color: "#64748b",
                textTransform: "none",
                borderRadius: 1.5,
                "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
              }}
            >
              {t("Cancel")}
            </Button>
            {usageType === "COMPLETE-PRODUCT" && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ViewModuleIcon />}
                disabled={!prdId}
                onClick={() => {
                  if (!prdId) {
                    setSnackbar({ open: true, message: t("Please save the product first"), severity: "warning" })
                    return
                  }
                  navigate(`/visual-editor/${prdId}`, {
                    state: {
                      product: {
                        id: prdId,
                        name,
                        steps,
                      },
                      types,
                      materialsList,
                    }
                  })
                }}
                sx={{
                  borderColor: "#10b981",
                  color: "#10b981",
                  textTransform: "none",
                  borderRadius: 1.5,
                  "&:hover": { borderColor: "#059669", bgcolor: "#ecfdf5" },
                }}
              >
                {t("Visual Editor")}
              </Button>
            )}
            {(usageType === "MATERIAL" || usageType === "PURCHASED-MATERIAL") && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<SyncAltIcon />}
                onClick={handleUpdateAll}
                disabled={isLoading || isSaving}
                sx={{
                  borderColor: "#8b5cf6",
                  color: "#8b5cf6",
                  textTransform: "none",
                  borderRadius: 1.5,
                  "&:hover": { borderColor: "#7c3aed", bgcolor: "#faf5ff" },
                }}
              >
                {t("Update All Products")}
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon fontSize="small" />}
              onClick={handleSave}
              disabled={isSaving || isLoading}
              sx={{
                bgcolor: "#3b82f6",
                textTransform: "none",
                borderRadius: 1.5,
                "&:hover": { bgcolor: "#2563eb" },
              }}
            >
              {isSaving ? t("Saving...") : t("Save Changes")}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={1.5}>
        {/* Left Column - Basic Info & Pricing */}
        <Grid item xs={12} lg={6}>
          {/* Basic Information */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: "#dbeafe", color: "#3b82f6" }}>
                <InventoryIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                  {t("Basic Information")}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  {t("Product details and classification")}
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("Product Name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("SKU")}
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("Provider SKU")}
                  value={provsku}
                  onChange={(e) => setProvSku(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("Description")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label={t("Usage Type")}
                  value={usageType}
                  onChange={(e) => setUsageType(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                  {usageTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {opt.icon}
                        <span>{opt.label}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          {/* Product Categories Assignment Card */}
          {prdId && (
            <ProductCategoryAssigner
              productId={prdId}
              onClose={() => {}}
            />
          )}

          {/* Pricing Card */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: "#dcfce7", color: "#16a34a" }}>
                <AttachMoneyIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                  {t("Pricing & Units")}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  {t("Configure pricing and measurement")}
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("Sales Price")}
                  type="number"
                  value={salesPrice}
                  onChange={(e) => setSalesPrice(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("B2B Price")}
                  type="number"
                  value={b2bSalesPrice}
                  onChange={(e) => setB2bSalesPrice(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("Cost Margin")}
                  type="number"
                  value={costMargin}
                  onChange={(e) => setCostMargin(e.target.value)}
                  helperText={t("Applied to calculated cost")}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("Cost Price")}
                  type="number"
                  value={costPrice}
                  onChange={(e) => usageType === "PURCHASED-MATERIAL" && setCostPrice(e.target.value)}
                  disabled={usageType !== "PURCHASED-MATERIAL"}
                  helperText={usageType !== "PURCHASED-MATERIAL" ? t("Auto-calculated") : t("Enter manually")}
                  InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label={t("VAT Category")}
                  value={vatCategoryId}
                  onChange={(e) => {
                    const selectedId = e.target.value
                    setVatCategoryId(selectedId)
                    const selectedCat = vatCategories.find(cat => cat.id === selectedId)
                    if (selectedCat) {
                      setVat(selectedCat.rate)
                      setVatClass(selectedCat.code)
                    }
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                  <MenuItem value="">{t("Select VAT Category")}</MenuItem>
                  {vatCategories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.label} - {cat.rate}%
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("VAT Rate")}
                  type="number"
                  value={vat}
                  disabled
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  helperText={t("Auto-filled from VAT category")}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label={t("Unit")}
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                  {unitOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          {/* Options - Collapsible */}
          <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Box
              onClick={() => setShowOptions(!showOptions)}
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                bgcolor: showOptions ? "#f8fafc" : "transparent",
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#fef3c7", color: "#d97706" }}>
                  <SettingsIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Options")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {t("Additional settings and customer link")}
                  </Typography>
                </Box>
              </Stack>
              <IconButton size="small" sx={{ color: "#64748b" }}>
                {showOptions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={showOptions} unmountOnExit>
              <Box sx={{ p: 2.5, borderTop: "1px solid #e2e8f0" }}>
                {/* Boolean Options */}
                <Typography variant="caption" fontWeight={600} color="#64748b" sx={{ mb: 1.5, display: "block" }}>
                  {t("Product Options")}
                </Typography>
                <Stack direction="row" spacing={3} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
                  <FormControlLabel
                    control={<Switch checked={overrideable} onChange={(e) => setOverrideable(e.target.checked)} color="primary" size="small" />}
                    label={<Typography variant="body2" color="#475569">{t("Can be overridden on tasks")}</Typography>}
                  />
                  {(usageType === "MATERIAL" || usageType === "PURCHASED-MATERIAL") && (
                    <FormControlLabel
                      control={<Switch checked={canLended} onChange={(e) => setCanLended(e.target.checked)} color="primary" size="small" />}
                      label={<Typography variant="body2" color="#475569">{t("Can be lended")}</Typography>}
                    />
                  )}
                </Stack>

                {/* Customer Link */}
                <Divider sx={{ my: 2, borderColor: "#f1f5f9" }} />
                <Typography variant="caption" fontWeight={600} color="#64748b" sx={{ mb: 1.5, display: "block" }}>
                  {t("Customer Link")}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    <CustomerSelect customers={customers} onSelect={(e) => setCustomerId(e)} />
                  </Box>
                  {customerName && (
                    <Chip label={customerName} size="small" sx={{ bgcolor: "#dbeafe", color: "#1d4ed8" }} />
                  )}
                </Stack>
              </Box>
            </Collapse>
          </Paper>

          {/* Dimensions - Collapsible */}
          <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Box
              onClick={() => setShowDimensions(!showDimensions)}
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                bgcolor: showDimensions ? "#f8fafc" : "transparent",
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#dbeafe", color: "#3b82f6" }}>
                  <StraightenIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Dimensions")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {t("Product and packaging dimensions")}
                  </Typography>
                </Box>
              </Stack>
              <IconButton size="small" sx={{ color: "#64748b" }}>
                {showDimensions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={showDimensions} unmountOnExit>
              <Box sx={{ p: 2.5, borderTop: "1px solid #e2e8f0" }}>
                {/* Product Dimensions */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <StraightenIcon sx={{ fontSize: 18, color: "#64748b" }} />
                  <Typography variant="subtitle2" fontWeight={600} color="#475569">
                    {t("Product Dimensions")}
                  </Typography>
                </Stack>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Length")}
                      type="number"
                      value={productDepth}
                      onChange={(e) => setProductLength(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Width")}
                      type="number"
                      value={productWidth}
                      onChange={(e) => setProductWidth(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Height")}
                      type="number"
                      value={productHeight}
                      onChange={(e) => setProductHeight(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Weight")}
                      type="number"
                      value={productWeight}
                      onChange={(e) => setProductWeight(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>

                {/* Packaging Dimensions */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Inventory2Icon sx={{ fontSize: 18, color: "#64748b" }} />
                  <Typography variant="subtitle2" fontWeight={600} color="#475569">
                    {t("Packaging Dimensions")}
                  </Typography>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Length")}
                      type="number"
                      value={packagingLength}
                      onChange={(e) => setPackagingLength(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Width")}
                      type="number"
                      value={packagingWidth}
                      onChange={(e) => setPackagingWidth(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Height")}
                      type="number"
                      value={packagingHeight}
                      onChange={(e) => setPackagingHeight(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Weight")}
                      type="number"
                      value={packagingWeight}
                      onChange={(e) => setPackagingWeight(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Paper>

          {/* Dimension Variants - Collapsible */}
          <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Box
              onClick={() => setShowVariants(!showVariants)}
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                bgcolor: showVariants ? "#f8fafc" : "transparent",
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#fce7f3", color: "#db2777" }}>
                  <ViewListIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Dimension Variants")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {variants.length > 0
                      ? `${variants.length} ${t("variants")}`
                      : t("Multiple sizes with own SKU and price")}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowVariants(true)
                    setVariants([...variants, { sku: "", depth: "", width: "", height: "", salesprice: "", b2bsalesprice: "", stock: 0 }])
                  }}
                  sx={{
                    bgcolor: "#db2777",
                    textTransform: "none",
                    borderRadius: 1.5,
                    fontSize: "0.75rem",
                    "&:hover": { bgcolor: "#be185d" },
                  }}
                >
                  {t("Add Variant")}
                </Button>
                <IconButton size="small" sx={{ color: "#64748b" }}>
                  {showVariants ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>
            </Box>

            <Collapse in={showVariants} unmountOnExit>
              <Box sx={{ borderTop: "1px solid #e2e8f0" }}>
                {variants.length === 0 ? (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: "#f8fafc",
                      m: 2,
                      borderRadius: 2,
                      border: "1px dashed #e2e8f0",
                    }}
                  >
                    <ViewListIcon sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }} />
                    <Typography variant="body2" color="#64748b">
                      {t("No variants added yet. Click 'Add Variant' to create size options.")}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("SKU")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("Depth")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("Width")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("Height")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("Sales Price")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("B2B Price")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("Stock")}</TableCell>
                          <TableCell sx={{ width: 48, py: 1 }} />
                        </TableRow>
                        {variants.map((v, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                value={v.sku ?? ""}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, sku: e.target.value }
                                  setVariants(updated)
                                }}
                                placeholder="SKU"
                                sx={{ minWidth: 120, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={v.depth ?? ""}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, depth: e.target.value }
                                  setVariants(updated)
                                }}
                                placeholder="mm"
                                sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={v.width ?? ""}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, width: e.target.value }
                                  setVariants(updated)
                                }}
                                placeholder="mm"
                                sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={v.height ?? ""}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, height: e.target.value }
                                  setVariants(updated)
                                }}
                                placeholder="mm"
                                sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={v.salesprice ?? ""}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, salesprice: e.target.value }
                                  setVariants(updated)
                                }}
                                placeholder="0.00"
                                InputProps={{ startAdornment: <InputAdornment position="start" sx={{ "& p": { fontSize: "0.75rem" } }}>€</InputAdornment> }}
                                sx={{ width: 100, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={v.b2bsalesprice ?? ""}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, b2bsalesprice: e.target.value }
                                  setVariants(updated)
                                }}
                                placeholder="0.00"
                                InputProps={{ startAdornment: <InputAdornment position="start" sx={{ "& p": { fontSize: "0.75rem" } }}>€</InputAdornment> }}
                                sx={{ width: 100, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={v.stock ?? 0}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, stock: parseInt(e.target.value) || 0 }
                                  setVariants(updated)
                                }}
                                placeholder="0"
                                sx={{ width: 70, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" }, min: 0 }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <IconButton
                                size="small"
                                onClick={() => setVariants(variants.filter((_, i) => i !== idx))}
                                sx={{ color: "#ef4444" }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Paper>

          {/* Pytha 3D File - Collapsible */}
          <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Box
              onClick={() => setShowPytha3D(!showPytha3D)}
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                bgcolor: showPytha3D ? "#f8fafc" : "transparent",
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#ede9fe", color: "#8b5cf6" }}>
                  <ViewInArIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Pytha 3D Model")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {pytha3DFile
                      ? pytha3DFile.original_filename || pytha3DFile.name
                      : t("Upload a Pytha 3D file for this product")}
                  </Typography>
                </Box>
              </Stack>
              <IconButton size="small" sx={{ color: "#64748b" }}>
                {showPytha3D ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={showPytha3D} unmountOnExit>
              <Box sx={{ p: 2.5, borderTop: "1px solid #e2e8f0" }}>
                <Pytha3DFileUpload
                  productId={prdId}
                  existingFile={pytha3DFile}
                  onFileUploaded={(f) => setPytha3DFile(f)}
                  onFileRemoved={() => setPytha3DFile(null)}
                />
              </Box>
            </Collapse>
          </Paper>

          {/* Compilable With Section - For COMPLETE-PRODUCT type */}
          {usageType === "COMPLETE-PRODUCT" && (
            <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
              {/* Header with Toggle */}
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: canBeCompiled ? "#f0fdf4" : "transparent",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ width: 36, height: 36, bgcolor: canBeCompiled ? "#d1fae5" : "#f3f4f6", color: canBeCompiled ? "#059669" : "#9ca3af" }}>
                    <LayersIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                      {t("Compilable With")}
                    </Typography>
                    <Typography variant="caption" color="#64748b">
                      {t("Products this can be compiled with")}
                    </Typography>
                  </Box>
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canBeCompiled}
                      onChange={(e) => setCanBeCompiled(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="body2" color="#475569">
                      {t("Can be Compiled")}
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ mr: 0 }}
                />
              </Box>

              {/* Collapsible Content */}
              <Collapse in={canBeCompiled} unmountOnExit>
                <Box sx={{ p: 2.5, borderTop: "1px solid #e2e8f0" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="body2" color="#64748b">
                      {compilableWith.length > 0
                        ? `${compilableWith.length} ${t("products linked")}`
                        : t("No products linked yet")}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setCompilableDrawerOpen(true)}
                      sx={{ bgcolor: "#059669", textTransform: "none", borderRadius: 2, "&:hover": { bgcolor: "#047857" } }}
                    >
                      {t("Add Product")}
                    </Button>
                  </Stack>

                  {compilableWith.length > 0 ? (
                    <Stack spacing={1}>
                      {compilableWith.map((item) => (
                        <Paper
                          key={item.id}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            borderColor: "#e2e8f0",
                            display: "flex",
                            alignItems: "center",
                            "&:hover": { borderColor: "#059669", bgcolor: "#f0fdf4" },
                          }}
                        >
                          {/* Product Photo */}
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 1.5,
                              bgcolor: "#f3f4f6",
                              overflow: "hidden",
                              flexShrink: 0,
                              mr: 1.5,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {item.photo ? (
                              <img
                                src={item.photo}
                                alt={item.name}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <ShoppingCartIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                            )}
                          </Box>

                          {/* Product Info */}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} color="#1e293b">
                              {item.name}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {item.sku && (
                                <Typography variant="caption" color="#64748b">
                                  SKU: {item.sku}
                                </Typography>
                              )}
                              {(item.productDepth || item.productWidth || item.productHeight) && (
                                <Typography variant="caption" color="#64748b">
                                  <StraightenIcon sx={{ fontSize: 10, mr: 0.3, verticalAlign: "middle" }} />
                                  {item.productDepth || 0}×{item.productWidth || 0}×{item.productHeight || 0}
                                </Typography>
                              )}
                            </Stack>
                          </Box>

                          <IconButton
                            size="small"
                            onClick={() => handleRemoveCompilableProduct(item.id)}
                            sx={{ color: "#dc2626" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 3, bgcolor: "#f8fafc", borderRadius: 2, border: "1px dashed #e2e8f0" }}>
                      <LayersIcon sx={{ fontSize: 32, color: "#cbd5e1", mb: 0.5 }} />
                      <Typography variant="body2" color="#94a3b8">
                        {t("Add products this can be compiled with")}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Paper>
          )}

          {/* Product Photos */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: "#fce7f3", color: "#db2777" }}>
                <ImageIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                {t("Product Photos")}
              </Typography>
            </Stack>

            <ProductImageManager
              images={productPhotos}
              onImagesChange={setProductPhotos}
            />
          </Paper>
        </Grid>

        {/* Right Column - Production Steps */}
        <Grid item xs={12} lg={6}>
          {/* Cost Summary Card - For production types (not COMPILED-PRODUCT) */}
          {usageType !== "PURCHASED-MATERIAL" && usageType !== "SERVICE" && usageType !== "COMPILED-PRODUCT" && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 1.5,
                borderRadius: 2,
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                color: "white",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {t("Calculated Cost")}
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    €{costPrice ? Number(costPrice).toFixed(2) : "0.00"}
                  </Typography>
                </Box>
                <AttachMoneyIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Stack>
            </Paper>
          )}

          {/* Service Total Card - For service type */}
          {usageType === "SERVICE" && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 1.5,
                borderRadius: 2,
                background: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)",
                color: "white",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {t("Service Total")}
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    €{calculateServiceTotal().toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {serviceTasks.length} {t("tasks")}
                  </Typography>
                </Box>
                <MiscellaneousServicesIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Stack>
            </Paper>
          )}

          {/* Compiled Total Card - For COMPILED-PRODUCT type */}
          {usageType === "COMPILED-PRODUCT" && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 1.5,
                borderRadius: 2,
                background: "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                color: "white",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {t("Compiled Total")} {costMargin ? `(+${costMargin}% ${t("margin")})` : ""}
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    €{calculateCompiledTotal().toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {compiledItems.length} {t("items")}
                  </Typography>
                </Box>
                <LayersIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Stack>
            </Paper>
          )}

          {/* Service Tasks - For service type */}
          {usageType === "SERVICE" && (
            <Paper elevation={0} sx={{ p: 2.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#fce7f3", color: "#db2777" }}>
                  <MiscellaneousServicesIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Service Tasks")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {t("Define tasks included in this service")}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenTaskDialog()}
                  sx={{ bgcolor: "#3b82f6", textTransform: "none", borderRadius: 2, "&:hover": { bgcolor: "#2563eb" } }}
                >
                  {t("Add Task")}
                </Button>
              </Stack>

              {serviceTasks.length > 0 ? (
                <Stack spacing={1.5}>
                  {serviceTasks.map((task, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        borderColor: "#e2e8f0",
                        "&:hover": { borderColor: "#cbd5e1", bgcolor: "#fafafa" },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
                            {task.name}
                          </Typography>
                          {task.description && (
                            <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
                              {task.description}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                            {task.duration && (
                              <Chip
                                icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                                label={`${task.duration} min`}
                                size="small"
                                sx={{ bgcolor: "#dbeafe", color: "#1d4ed8" }}
                              />
                            )}
                            {task.price && (
                              <Chip
                                icon={<AttachMoneyIcon sx={{ fontSize: 14 }} />}
                                label={`€${Number(task.price).toFixed(2)}`}
                                size="small"
                                sx={{ bgcolor: "#dcfce7", color: "#16a34a" }}
                              />
                            )}
                            {task.file && (
                              <Chip
                                icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                                label={t("File")}
                                size="small"
                                component="a"
                                href={task.file}
                                target="_blank"
                                clickable
                                sx={{ bgcolor: "#fef3c7", color: "#d97706" }}
                              />
                            )}
                            {task.photo && (
                              <Chip
                                icon={<PhotoCameraIcon sx={{ fontSize: 14 }} />}
                                label={t("Photo")}
                                size="small"
                                component="a"
                                href={task.photo}
                                target="_blank"
                                clickable
                                sx={{ bgcolor: "#ede9fe", color: "#8b5cf6" }}
                              />
                            )}
                          </Stack>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" onClick={() => handleOpenTaskDialog(index)} sx={{ color: "#64748b" }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteTask(index)} sx={{ color: "#dc2626" }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: "center", py: 4, bgcolor: "#f8fafc", borderRadius: 2, border: "1px dashed #e2e8f0" }}>
                  <MiscellaneousServicesIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                  <Typography variant="body2" color="#94a3b8">
                    {t("No tasks defined")}
                  </Typography>
                  <Typography variant="caption" color="#94a3b8">
                    {t("Add tasks to define what this service includes")}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Providers Section - For PURCHASED-MATERIAL type */}
          {usageType === "PURCHASED-MATERIAL" && (
            <Paper elevation={0} sx={{ p: 2.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#fef3c7", color: "#d97706" }}>
                  <LocalShippingIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Suppliers")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {selectedProviders.length > 0
                      ? `${selectedProviders.length} ${t("suppliers linked")}`
                      : t("Link suppliers for this material")}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setProviderDrawerOpen(true)}
                  sx={{ bgcolor: "#d97706", textTransform: "none", borderRadius: 2, "&:hover": { bgcolor: "#b45309" } }}
                >
                  {t("Add Supplier")}
                </Button>
              </Stack>

              {selectedProviders.length > 0 ? (
                <Stack spacing={1}>
                  {selectedProviders.map((provider) => (
                    <Paper
                      key={provider.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        borderColor: "#e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        "&:hover": { borderColor: "#d97706", bgcolor: "#fffbeb" },
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 32, height: 32, bgcolor: "#fef3c7", color: "#d97706" }}>
                          <StorefrontIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Typography variant="body2" fontWeight={500} color="#1e293b">
                          {provider.name}
                        </Typography>
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveProvider(provider.id)}
                        sx={{ color: "#dc2626" }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: "center", py: 4, bgcolor: "#f8fafc", borderRadius: 2, border: "1px dashed #e2e8f0" }}>
                  <LocalShippingIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                  <Typography variant="body2" color="#94a3b8">
                    {t("No suppliers linked")}
                  </Typography>
                  <Typography variant="caption" color="#94a3b8">
                    {t("Add suppliers that provide this material")}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Compiled Items Section - For COMPILED-PRODUCT type */}
          {usageType === "COMPILED-PRODUCT" && (
            <Paper elevation={0} sx={{ p: 2.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#d1fae5", color: "#059669" }}>
                  <LayersIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Compiled Items")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {compiledItems.length > 0
                      ? `${compiledItems.length} ${t("products/services included")}`
                      : t("Add products or services to compile")}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCompilerDrawerOpen(true)}
                  sx={{ bgcolor: "#059669", textTransform: "none", borderRadius: 2, "&:hover": { bgcolor: "#047857" } }}
                >
                  {t("Add Item")}
                </Button>
              </Stack>

              {compiledItems.length > 0 ? (
                <Stack spacing={1.5}>
                  {compiledItems.map((item) => (
                    <Paper
                      key={item.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        borderColor: "#e2e8f0",
                        "&:hover": { borderColor: "#059669", bgcolor: "#f0fdf4" },
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        {/* Product Photo */}
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: 2,
                            bgcolor: "#f3f4f6",
                            overflow: "hidden",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {item.photo ? (
                            <img
                              src={item.photo}
                              alt={item.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <ViewModuleIcon sx={{ fontSize: 24, color: "#9ca3af" }} />
                          )}
                        </Box>

                        {/* Product Info */}
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
                              {item.name}
                            </Typography>
                            <Chip
                              label={item.usageType === "SERVICE" ? t("Service") : t("Product")}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                bgcolor: item.usageType === "SERVICE" ? "#ede9fe" : "#dcfce7",
                                color: item.usageType === "SERVICE" ? "#7c3aed" : "#166534",
                              }}
                            />
                          </Stack>

                          {/* Dimensions */}
                          {(item.productDepth || item.productWidth || item.productHeight) && (
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="#64748b">
                                <StraightenIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: "middle" }} />
                                {item.productDepth || 0} × {item.productWidth || 0} × {item.productHeight || 0} cm
                              </Typography>
                            </Stack>
                          )}

                          <Stack direction="row" spacing={2} sx={{ mt: 1 }} alignItems="center">
                            <Chip
                              icon={<AttachMoneyIcon sx={{ fontSize: 14 }} />}
                              label={`€${Number(item.costprice || 0).toFixed(2)}`}
                              size="small"
                              sx={{ bgcolor: "#dbeafe", color: "#1d4ed8" }}
                            />
                          </Stack>
                        </Box>

                        {/* Quantity */}
                        <TextField
                          size="small"
                          type="number"
                          label={t("Qty")}
                          value={item.quantity || 1}
                          onChange={(e) => handleCompiledItemQuantityChange(item.id, e.target.value)}
                          inputProps={{ min: 1, style: { textAlign: "center" } }}
                          sx={{ width: 70, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />

                        {/* Item Total */}
                        <Box sx={{ textAlign: "right", minWidth: 80 }}>
                          <Typography variant="caption" color="#64748b">
                            {t("Subtotal")}
                          </Typography>
                          <Typography variant="subtitle2" fontWeight={600} color="#059669">
                            €{((item.costprice || 0) * (item.quantity || 1)).toFixed(2)}
                          </Typography>
                        </Box>

                        <IconButton
                          size="small"
                          onClick={() => handleRemoveCompiledItem(item.id)}
                          sx={{ color: "#dc2626" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: "center", py: 4, bgcolor: "#f8fafc", borderRadius: 2, border: "1px dashed #e2e8f0" }}>
                  <LayersIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                  <Typography variant="body2" color="#94a3b8">
                    {t("No items added")}
                  </Typography>
                  <Typography variant="caption" color="#94a3b8">
                    {t("Add products or services to create a compiled product")}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Production Steps - For production types */}
          {usageType !== "PURCHASED-MATERIAL" && usageType !== "SERVICE" && usageType !== "COMPILED-PRODUCT" && (
            <Paper elevation={0} sx={{ p: 2.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#ede9fe", color: "#8b5cf6" }}>
                  <SettingsIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Production Steps")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {t("Define production process")}
                  </Typography>
                </Box>
              </Stack>

              {/* Add Step Controls */}
              <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                  select
                  size="small"
                  label={t("Select production Section")}
                  value={newStepType}
                  onChange={(e) => setNewStepType(e.target.value)}
                  sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                  {types.map((type, i) => (
                    <MenuItem key={i} value={type.name}>{type.name}</MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddStep}
                  disabled={!newStepType || types.length === 0}
                  sx={{ bgcolor: "#3b82f6", textTransform: "none", borderRadius: 2, "&:hover": { bgcolor: "#2563eb" } }}
                >
                  {t("Add Step")}
                </Button>
              </Stack>

              {/* Steps List */}
              <Stack spacing={1.5}>
                {steps.map((step, stepIndex) => (
                  <StyledStepCard key={stepIndex} elevation={0}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ bgcolor: "#3b82f6", width: 28, height: 28, fontSize: 12 }}>
                          {step.seq}
                        </Avatar>
                        <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
                          {step.typid}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon fontSize="small" />}
                          onClick={() => { setSelectedStep(stepIndex); setDrawerOpen(true) }}
                          sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b", fontSize: "0.75rem" }}
                        >
                          {t("Materials")}
                        </Button>
                        <IconButton size="small" color="error" onClick={() => handleDeleteStep(stepIndex)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>

                    {/* Materials in Step */}
                    {step.materials && step.materials.length > 0 ? (
                      <Stack spacing={1}>
                        {step.materials.map((mat, matIndex) => (
                          <Paper
                            key={matIndex}
                            variant="outlined"
                            sx={{ p: 1.5, borderRadius: 2, display: "flex", alignItems: "center", gap: 1.5, borderColor: "#e2e8f0" }}
                          >
                            <MaterialChip
                              materialType={mat.usageType}
                              icon={getMaterialTypeIcon(mat.usageType)}
                              label={mat.usageType?.replace("-", " ")}
                              size="small"
                            />
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, color: "#1e293b" }}>
                              {mat.name}
                            </Typography>
                            <Chip label={mat.unit} size="small" variant="outlined" sx={{ borderColor: "#e2e8f0", color: "#64748b" }} />
                            {mat.overidable && (
                              <Tooltip title={t("Can be overridden")}>
                                <SyncAltIcon sx={{ color: "#16a34a", fontSize: 18 }} />
                              </Tooltip>
                            )}
                            <TextField
                              size="small"
                              type="text"
                              defaultValue={mat.quantity}
                              onChange={(e) => handleMaterialQuantityChange(stepIndex, matIndex, e.target.value)}
                              onBlur={calculateTotalCost}
                              sx={{ width: 70, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                              inputProps={{ style: { textAlign: "center", fontSize: "0.875rem" } }}
                            />
                            <IconButton size="small" color="error" onClick={() => handleDeleteMaterial(stepIndex, matIndex)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 2, color: "#94a3b8" }}>
                        <Typography variant="body2">{t("No materials added")}</Typography>
                      </Box>
                    )}

                    {/* Step Instructions */}
                    <Divider sx={{ my: 1.5, borderColor: "#f1f5f9" }} />
                    <Typography variant="caption" color="#64748b" fontWeight={600} sx={{ mb: 1, display: "block" }}>
                      {t("Instructions")}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      placeholder={t("Add production instructions for this step...")}
                      value={step.instructions || ""}
                      onChange={(e) => handleStepInstructionsChange(stepIndex, e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />

                    {/* Step Files */}
                    <Divider sx={{ my: 1.5, borderColor: "#f1f5f9" }} />
                    <Typography variant="caption" color="#64748b" fontWeight={600} sx={{ mb: 1, display: "block" }}>
                      {t("Attachments")}
                    </Typography>
                    <StepFileUpload
                      files={step.files || []}
                      onFilesChange={(newFiles) => handleStepFilesChange(stepIndex, newFiles)}
                      stepName={step.typid}
                    />
                  </StyledStepCard>
                ))}

                {steps.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 4, color: "#94a3b8" }}>
                    <BuildIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2">{t("No production steps defined")}</Typography>
                    <Typography variant="caption">{t("Add steps to define the production process")}</Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {/* Validation Alerts */}
          {((!costPrice || costPrice < 0) && usageType !== "PURCHASED-MATERIAL") || (steps[0] && steps[0].seq !== 1) || isLoading ? (
            <Stack spacing={1}>
              {(!costPrice || costPrice < 0) && usageType !== "PURCHASED-MATERIAL" && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  {t("Cost price is not set or invalid")}
                </Alert>
              )}

              {steps[0] && steps[0].seq !== 1 && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {t("Step sequence should start with number 1")}
                </Alert>
              )}

              {isLoading && (
                <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ borderRadius: 2 }}>
                  {updateAllLoader || t("Updating all products...")}
                </Alert>
              )}
            </Stack>
          ) : null}
        </Grid>
      </Grid>

      {/* Materials Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSearch("") }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 380 }, borderRadius: "12px 0 0 12px" } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={600} color="#1e293b">
              {t("Select Material")}
            </Typography>
            <IconButton size="small" onClick={() => { setDrawerOpen(false); setSearch("") }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <TextField
            fullWidth
            size="small"
            placeholder={t("Search materials...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="caption" color="#64748b" sx={{ mb: 1.5, display: "block" }}>
            {filteredMaterials.length} {t("materials found")}
          </Typography>

          <List sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
            {filteredMaterials.map((mat, i) => (
              <ListItem
                key={i}
                button
                onClick={() => handleSelectMaterial(mat)}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  border: "1px solid #e2e8f0",
                  "&:hover": { bgcolor: "#f8fafc", borderColor: "#3b82f6" },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 1.5 }}>
                  {getMaterialTypeIcon(mat.usageType)}
                </Stack>
                <ListItemText
                  primary={<Typography variant="body2" fontWeight={500} color="#1e293b">{mat.name}</Typography>}
                  secondary={
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <MaterialChip materialType={mat.usageType} label={mat.usageType?.replace("-", " ")} size="small" />
                      <Chip label={mat.unit} size="small" variant="outlined" sx={{ borderColor: "#e2e8f0", color: "#64748b" }} />
                    </Stack>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Provider Selection Drawer */}
      <Drawer
        anchor="right"
        open={providerDrawerOpen}
        onClose={() => { setProviderDrawerOpen(false); setProviderSearch("") }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 380 }, borderRadius: "12px 0 0 12px" } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={600} color="#1e293b">
              {t("Select Supplier")}
            </Typography>
            <IconButton size="small" onClick={() => { setProviderDrawerOpen(false); setProviderSearch("") }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <TextField
            fullWidth
            size="small"
            placeholder={t("Search suppliers...")}
            value={providerSearch}
            onChange={(e) => setProviderSearch(e.target.value)}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="caption" color="#64748b" sx={{ mb: 1.5, display: "block" }}>
            {filteredProviders.length} {t("suppliers found")}
          </Typography>

          <List sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
            {filteredProviders.map((provider) => {
              const isSelected = selectedProviders.some((p) => p.id === provider.id)
              return (
                <ListItem
                  key={provider.id}
                  button
                  onClick={() => !isSelected && handleAddProvider(provider)}
                  disabled={isSelected}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    border: "1px solid #e2e8f0",
                    bgcolor: isSelected ? "#f8fafc" : "transparent",
                    "&:hover": { bgcolor: "#fffbeb", borderColor: "#d97706" },
                    "&.Mui-disabled": { opacity: 0.6 },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mr: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: "#fef3c7", color: "#d97706" }}>
                      <StorefrontIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                  </Stack>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500} color="#1e293b">
                        {provider.name}
                      </Typography>
                    }
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        {provider.email && (
                          <Typography variant="caption" color="#64748b">
                            {provider.email}
                          </Typography>
                        )}
                        {isSelected && (
                          <Chip label={t("Added")} size="small" sx={{ bgcolor: "#dcfce7", color: "#166534", height: 20 }} />
                        )}
                      </Stack>
                    }
                  />
                </ListItem>
              )
            })}
            {filteredProviders.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <LocalShippingIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                <Typography variant="body2" color="#94a3b8">
                  {t("No suppliers found")}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </Drawer>

      {/* Compiler Drawer - For COMPILED-PRODUCT */}
      <Drawer
        anchor="right"
        open={compilerDrawerOpen}
        onClose={() => { setCompilerDrawerOpen(false); setCompilerSearch("") }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, borderRadius: "12px 0 0 12px" } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={600} color="#1e293b">
              {t("Add Product or Service")}
            </Typography>
            <IconButton size="small" onClick={() => { setCompilerDrawerOpen(false); setCompilerSearch("") }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <TextField
            fullWidth
            size="small"
            placeholder={t("Search products and services...")}
            value={compilerSearch}
            onChange={(e) => setCompilerSearch(e.target.value)}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="caption" color="#64748b" sx={{ mb: 1.5, display: "block" }}>
            {filteredAvailableProducts.length} {t("items available")}
          </Typography>

          <List sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
            {filteredAvailableProducts.map((product) => {
              const isAlreadyAdded = compiledItems.some((item) => item.id === product.id)
              const photos = product.productPhotos ? JSON.parse(product.productPhotos) : []
              const photo = photos[0]?.url || null

              return (
                <ListItem
                  key={product.id}
                  button
                  onClick={() => !isAlreadyAdded && handleAddCompiledItem(product)}
                  disabled={isAlreadyAdded}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    border: "1px solid #e2e8f0",
                    bgcolor: isAlreadyAdded ? "#f8fafc" : "transparent",
                    "&:hover": { bgcolor: "#f0fdf4", borderColor: "#059669" },
                    "&.Mui-disabled": { opacity: 0.6 },
                    p: 1.5,
                  }}
                >
                  {/* Product Photo */}
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 1.5,
                      bgcolor: "#f3f4f6",
                      overflow: "hidden",
                      flexShrink: 0,
                      mr: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {photo ? (
                      <img
                        src={photo}
                        alt={product.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <ViewModuleIcon sx={{ fontSize: 20, color: "#9ca3af" }} />
                    )}
                  </Box>

                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" fontWeight={500} color="#1e293b">
                          {product.name}
                        </Typography>
                        <Chip
                          label={product.usageType === "SERVICE" ? t("Service") : t("Product")}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.65rem",
                            bgcolor: product.usageType === "SERVICE" ? "#ede9fe" : "#dcfce7",
                            color: product.usageType === "SERVICE" ? "#7c3aed" : "#166534",
                          }}
                        />
                        {isAlreadyAdded && (
                          <Chip label={t("Added")} size="small" sx={{ bgcolor: "#dbeafe", color: "#1d4ed8", height: 18 }} />
                        )}
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {/* Dimensions */}
                        {(product.productDepth || product.productWidth || product.productHeight) && (
                          <Typography variant="caption" color="#64748b">
                            <StraightenIcon sx={{ fontSize: 11, mr: 0.5, verticalAlign: "middle" }} />
                            {product.productDepth || 0} × {product.productWidth || 0} × {product.productHeight || 0} cm
                          </Typography>
                        )}
                        <Typography variant="caption" color="#059669" fontWeight={500}>
                          €{Number(product.costprice || 0).toFixed(2)}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
              )
            })}
            {filteredAvailableProducts.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <LayersIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                <Typography variant="body2" color="#94a3b8">
                  {t("No products or services found")}
                </Typography>
                <Typography variant="caption" color="#94a3b8">
                  {t("Create Complete Products or Services first")}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </Drawer>

      {/* Compilable With Drawer - For COMPLETE-PRODUCT */}
      <Drawer
        anchor="right"
        open={compilableDrawerOpen}
        onClose={() => { setCompilableDrawerOpen(false); setCompilableSearch("") }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 400 }, borderRadius: "12px 0 0 12px" } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={600} color="#1e293b">
              {t("Add Compilable Product")}
            </Typography>
            <IconButton size="small" onClick={() => { setCompilableDrawerOpen(false); setCompilableSearch("") }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <TextField
            fullWidth
            size="small"
            placeholder={t("Search complete products...")}
            value={compilableSearch}
            onChange={(e) => setCompilableSearch(e.target.value)}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="caption" color="#64748b" sx={{ mb: 1.5, display: "block" }}>
            {filteredCompilableProducts.length} {t("products available")}
          </Typography>

          <List sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
            {filteredCompilableProducts.map((product) => {
              const isAlreadyAdded = compilableWith.some((item) => item.id === product.id)
              const photos = product.productPhotos ? JSON.parse(product.productPhotos) : []
              const photo = photos[0]?.url || null

              return (
                <ListItem
                  key={product.id}
                  button
                  onClick={() => !isAlreadyAdded && handleAddCompilableProduct(product)}
                  disabled={isAlreadyAdded}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    border: "1px solid #e2e8f0",
                    bgcolor: isAlreadyAdded ? "#f8fafc" : "transparent",
                    "&:hover": { bgcolor: "#f0fdf4", borderColor: "#059669" },
                    "&.Mui-disabled": { opacity: 0.6 },
                    p: 1.5,
                  }}
                >
                  {/* Product Photo */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.5,
                      bgcolor: "#f3f4f6",
                      overflow: "hidden",
                      flexShrink: 0,
                      mr: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {photo ? (
                      <img
                        src={photo}
                        alt={product.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <ShoppingCartIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                    )}
                  </Box>

                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" fontWeight={500} color="#1e293b">
                          {product.name}
                        </Typography>
                        {isAlreadyAdded && (
                          <Chip label={t("Added")} size="small" sx={{ bgcolor: "#d1fae5", color: "#059669", height: 18 }} />
                        )}
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {product.sku && (
                          <Typography variant="caption" color="#64748b">
                            SKU: {product.sku}
                          </Typography>
                        )}
                        {(product.productDepth || product.productWidth || product.productHeight) && (
                          <Typography variant="caption" color="#64748b">
                            <StraightenIcon sx={{ fontSize: 11, mr: 0.5, verticalAlign: "middle" }} />
                            {product.productDepth || 0} × {product.productWidth || 0} × {product.productHeight || 0} cm
                          </Typography>
                        )}
                      </Stack>
                    }
                  />
                </ListItem>
              )
            })}
            {filteredCompilableProducts.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <ShoppingCartIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                <Typography variant="body2" color="#94a3b8">
                  {t("No complete products found")}
                </Typography>
                <Typography variant="caption" color="#94a3b8">
                  {t("Create Complete Products first")}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Service Task Dialog */}
      <Dialog
        open={taskDialogOpen}
        onClose={handleCloseTaskDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600} color="#1e293b">
              {editingTaskIndex !== null ? t("Edit Task") : t("Add New Task")}
            </Typography>
            <IconButton size="small" onClick={handleCloseTaskDialog}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t("Task Name")}
              value={currentTask.name}
              onChange={(e) => setCurrentTask({ ...currentTask, name: e.target.value })}
              fullWidth
              required
              placeholder={t("Enter task name...")}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            <TextField
              label={t("Description")}
              value={currentTask.description}
              onChange={(e) => setCurrentTask({ ...currentTask, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder={t("Describe the task...")}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label={t("Duration (minutes)")}
                  value={currentTask.duration}
                  onChange={(e) => setCurrentTask({ ...currentTask, duration: e.target.value })}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTimeIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t("Price")}
                  value={currentTask.price}
                  onChange={(e) => setCurrentTask({ ...currentTask, price: e.target.value })}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ color: "#94a3b8" }}>€</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Divider />

            <Typography variant="subtitle2" color="#475569" fontWeight={600}>
              {t("Attachments")}
            </Typography>

            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <input
                  type="file"
                  id="task-file-input"
                  style={{ display: "none" }}
                  onChange={(e) => handleTaskFileUpload(e, "file")}
                />
                <label htmlFor="task-file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<AttachFileIcon />}
                    sx={{
                      borderColor: "#e2e8f0",
                      color: "#64748b",
                      textTransform: "none",
                      borderRadius: 2,
                      py: 1.5,
                      "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" },
                    }}
                  >
                    {currentTask.file ? t("File Attached") : t("Attach File")}
                  </Button>
                </label>
                {currentTask.file && (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                    <Chip
                      label={t("File")}
                      size="small"
                      color="primary"
                      onDelete={() => setCurrentTask({ ...currentTask, file: null })}
                      sx={{ borderRadius: 1 }}
                    />
                  </Stack>
                )}
              </Box>

              <Box sx={{ flex: 1 }}>
                <input
                  type="file"
                  id="task-photo-input"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => handleTaskFileUpload(e, "photo")}
                />
                <label htmlFor="task-photo-input">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<PhotoCameraIcon />}
                    sx={{
                      borderColor: "#e2e8f0",
                      color: "#64748b",
                      textTransform: "none",
                      borderRadius: 2,
                      py: 1.5,
                      "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" },
                    }}
                  >
                    {currentTask.photo ? t("Photo Attached") : t("Attach Photo")}
                  </Button>
                </label>
                {currentTask.photo && (
                  <Box sx={{ mt: 1, borderRadius: 2, overflow: "hidden", border: "1px solid #e2e8f0" }}>
                    <img
                      src={currentTask.photo}
                      alt="Task photo"
                      style={{ width: "100%", height: 80, objectFit: "cover" }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setCurrentTask({ ...currentTask, photo: null })}
                      sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(255,255,255,0.9)" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={handleCloseTaskDialog}
            sx={{ color: "#64748b", textTransform: "none", borderRadius: 2 }}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTask}
            startIcon={<SaveIcon />}
            sx={{
              bgcolor: "#3b82f6",
              textTransform: "none",
              borderRadius: 2,
              "&:hover": { bgcolor: "#2563eb" },
            }}
          >
            {editingTaskIndex !== null ? t("Update Task") : t("Add Task")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MuiProductUpdate
